import { useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, authHeaders, describeError } from "../lib/api";
import type { AdminRequestRow, DocumentType, RequestStatus } from "../lib/api";
import { getCurrentUserId } from "../lib/auth";
import { fetchDaily } from "../lib/dashboard-data";
import { dateTimeLao, num, shortId } from "../lib/format";
import { QUEUE_STATUSES, awaitingVillage, formatAgoLo, villageOverdue } from "../lib/requestStatus";
import { KpiTile } from "../components/dx";
import { VillageWaiveModal } from "../components/VillageWaiveModal";
import { ArrowUpDown, Inbox, Lock } from "../components/icons";
import {
  canClaimRequests,
  canReviewRequests,
  canSignDocuments,
  seesApplicantPII,
} from "../lib/permissions";

async function fetchDocumentTypes(): Promise<DocumentType[]> {
  const { data, error, response } = await api.GET("/v1/document-types", {
    params: { query: { limit: 100 } },
  });
  if (error || !data) throw new Error(describeError(error, response.status));
  return data.items;
}

// ตาแสงระดับตำบลมีคำร้องค้างพร้อมกันไม่เกินหลักร้อย — ยังไม่ต้องทำหน้าเลื่อนหน้า
// (limit ตรงกับ maxPageLimit ฝั่ง API พอดี) ถ้า has_more จะเตือนไว้ใต้ตาราง
async function fetchQueue(): Promise<{ items: AdminRequestRow[]; hasMore: boolean }> {
  const { data, error, response } = await api.GET("/v1/admin/requests", {
    params: { query: { limit: 200, status: QUEUE_STATUSES } },
    headers: authHeaders(),
  });
  if (error || !data) throw new Error(describeError(error, response.status));
  return { items: data.items, hasMore: data.has_more };
}

/**
 * ขั้นตอนของคำร้องตาม mockup (`work_queue.js`) แปลงเป็นสถานะจริงของระบบ
 *
 * ขั้น ນາຍບ້ານຢັ້ງຢືນ (ADR-0031) เป็นสถานะย่อยของ `submitted` (`village_certification.status = pending`)
 * จึงแยกแท็บด้วย predicate ไม่ใช่รายการสถานะ — คำร้องที่ขั้นนี้ไม่ใช้ (`village_certification: null`) เข้า
 * ລໍຜູ້ກວດເອກະສານ ตามเดิม · ขั้น ລໍຊຳລະ ของ mockup ไม่มีในระบบ (ผู้ขอชำระตอนยื่น) จึงไม่ทำแท็บ
 */
const STAGES: { id: string; label: string; match: (r: AdminRequestRow) => boolean }[] = [
  { id: "village", label: "ລໍນາຍບ້ານຢັ້ງຢືນ", match: awaitingVillage },
  { id: "submitted", label: "ລໍຜູ້ກວດເອກະສານ", match: (r) => r.status === "submitted" && !awaitingVillage(r) },
  { id: "in_review", label: "ກຳລັງກວດ", match: (r) => r.status === "in_review" },
  { id: "approved", label: "ລໍລົງນາມ", match: (r) => r.status === "approved" },
  { id: "signed", label: "ລໍອອກເອກະສານ", match: (r) => r.status === "signed" },
  { id: "needs_changes", label: "ສົ່ງຄືນໃຫ້ແກ້ໄຂ", match: (r) => r.status === "needs_changes" },
  { id: "closed", label: "ປິດແລ້ວ", match: (r) => ["issued", "rejected", "cancelled"].includes(r.status) },
];
const OPEN: RequestStatus[] = ["submitted", "in_review", "needs_changes", "approved", "signed"];

const STAGE_LABEL: Record<RequestStatus, string> = {
  draft: "ຮ່າງ",
  submitted: "ລໍຜູ້ກວດເອກະສານ",
  in_review: "ກຳລັງກວດ",
  needs_changes: "ສົ່ງຄືນໃຫ້ແກ້ໄຂ",
  approved: "ລໍລົງນາມ",
  signed: "ລໍອອກເອກະສານ",
  issued: "ອອກເອກະສານແລ້ວ",
  rejected: "ປະຕິເສດ",
  cancelled: "ຍົກເລີກ",
};
const STAGE_TONE: Record<RequestStatus, string> = {
  draft: "",
  submitted: "q-badge--info",
  in_review: "q-badge--info",
  needs_changes: "q-badge--err",
  approved: "q-badge--purple",
  signed: "q-badge--teal",
  issued: "q-badge--ok",
  rejected: "q-badge--err",
  cancelled: "",
};

/** กำหนดเวลา: เกณฑ์เดียวกับ Dashboard — ค้างเกิน 7 วันนับจากวันยื่น (ไม่มี SLA แยกต่อขั้นในระบบ) */
const SLA_DAYS = 7;
const DAY_MS = 86_400_000;

function slaOf(r: AdminRequestRow, now: number): { tone: "ok" | "warn" | "err" | "muted"; text: string; over: boolean } {
  if (!OPEN.includes(r.status) || !r.submitted_at) return { tone: "muted", text: "—", over: false };
  // ລໍນາຍບ້ານ: ກຳນົດຂອງນາຍບ້ານ (2 ວັນເຮັດການ) ແທນ 7 ມື້ — ເກີນແລ້ວຕາແສງກົດຮັບໄດ້ (ລະບົບຂ້າມຂັ້ນໃຫ້)
  const vc = r.village_certification;
  if (awaitingVillage(r) && vc?.due_at) {
    const leftV = (new Date(vc.due_at).getTime() - now) / DAY_MS;
    if (leftV <= 0) return { tone: "err", text: "ນາຍບ້ານ ເກີນກຳນົດ", over: true };
    return { tone: leftV < 1 ? "warn" : "ok", text: `ນາຍບ້ານ ເຫຼືອ ${daysText(leftV)}`, over: false };
  }
  if (r.status === "needs_changes") return { tone: "muted", text: "ລໍຜູ້ຂໍແກ້ໄຂ", over: false };
  const age = (now - new Date(r.submitted_at).getTime()) / DAY_MS;
  const left = SLA_DAYS - age;
  if (left < 0) return { tone: "err", text: `ເກີນກຳນົດ ${daysText(-left)}`, over: true };
  if (left < 1) return { tone: "warn", text: `ໃກ້ຄົບກຳນົດ · ເຫຼືອ ${daysText(left)}`, over: false };
  return { tone: "ok", text: `ເຫຼືອ ${daysText(left)}`, over: false };
}

function daysText(d: number): string {
  if (d < 1) return `${String(Math.max(1, Math.round(d * 24)))} ຊົ່ວໂມງ`;
  return `${(Math.round(d * 10) / 10).toLocaleString("lo-LA")} ມື້`;
}

/** วิธีรับเอกสาร: form_data `delivery` (digital/paper) + จำนวนฉบับจากการชำระ */
function DeliveryBadge({ row }: { row: AdminRequestRow }) {
  const d = (row.form_data as Record<string, unknown>).delivery;
  if (d === "digital") return <span className="q-badge q-badge--info">ດິຈິຕອນ</span>;
  if (d === "paper")
    return <span className="q-badge q-badge--out num">{row.copies ? `ເຈ້ຍ × ${num(row.copies)}` : "ເຈ້ຍ"}</span>;
  return <span className="q-faint">—</span>;
}

/** เวลาที่ใช้เรียงคิว/แสดงผล — ยังไม่ยื่นก็ใช้เวลาสร้างแทน */
const stamp = (r: AdminRequestRow) => r.submitted_at ?? r.created_at;

/** ลำดับที่แสดงในคอลัมน์ ຂັ້ນຕອນ — ລໍນາຍບ້ານ แยกจาก ລໍຜູ້ກວດເອກະສານ ทั้งที่สถานะเดียวกัน */
function StageBadge({ row }: { row: AdminRequestRow }) {
  if (awaitingVillage(row)) return <span className="q-badge">ລໍນາຍບ້ານຢັ້ງຢືນ</span>;
  return <span className={`q-badge ${STAGE_TONE[row.status]}`}>{STAGE_LABEL[row.status]}</span>;
}

export function RequestsPage() {
  const me = getCurrentUserId();
  // ADR-0030: ปุ่ม/แท็บตามสิทธิ์ — รับเข้าคิว APP-06 · ตรวจ REV-02/03/04 · เซ็น REV-06 + can_sign
  const canClaim = canClaimRequests();
  const canReview = canClaim || canReviewRequests();
  const canSign = canSignDocuments();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();
  // เวลาอ้างอิงของกำหนดเวลา — คิดครั้งเดียวต่อการโหลดข้อมูล (ไม่เรียก Date.now() ระหว่าง render)
  const queue = useQuery({ queryKey: ["requests", "queue"], queryFn: fetchQueue });
  const now = queue.dataUpdatedAt;
  const types = useQuery({ queryKey: ["document-types"], queryFn: fetchDocumentTypes });
  const today = useQuery({
    queryKey: ["stats", "daily", "today|queue"],
    queryFn: () => fetchDaily({ kind: "today" }, {}),
  });
  const typeById = useMemo(() => new Map((types.data ?? []).map((t) => [t.id, t] as const)), [types.data]);

  const stage = params.get("stage") ?? (canReview ? "mine" : "all");
  const overOnly = params.get("over") === "1";
  const [q, setQ] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [village, setVillage] = useState("");
  const [newestFirst, setNewestFirst] = useState(false);
  const [waiving, setWaiving] = useState<AdminRequestRow | null>(null);

  function setParam(k: string, v: string | null) {
    const next = new URLSearchParams(params);
    if (v === null) next.delete(k);
    else next.set(k, v);
    setParams(next, { replace: true });
  }

  const claim = useMutation<unknown, Error, string>({
    mutationFn: async (id) => {
      const { data, error, response } = await api.POST("/v1/admin/requests/{id}/transitions", {
        params: { path: { id } },
        body: { to: "in_review" },
        headers: authHeaders(),
      });
      if (error || !data) throw new Error(describeError(error, response.status));
      return data;
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["requests"] }),
  });

  const items = useMemo(() => queue.data?.items ?? [], [queue.data]);
  const open = items.filter((r) => OPEN.includes(r.status));
  // ວຽກຂອງຂ້ອຍ = ເລື່ອງທີ່ຍັງບໍ່ມີຄົນຮັບ (ບໍ່ນັບທີ່ລໍນາຍບ້ານ — ຍັງຮັບບໍ່ໄດ້) + ທີ່ຂ້ອຍຮັບໄວ້ (ກວດ/ລໍອອກ)
  // + ລໍລົງນາມ ຖ້າຂ້ອຍເປັນຜູ້ເຊັນ
  const isMine = (r: AdminRequestRow) =>
    (r.status === "submitted" && !awaitingVillage(r)) ||
    ((r.status === "in_review" || r.status === "signed") && r.assigned_officer_id === me) ||
    (r.status === "approved" && canSign);
  const inStage = (r: AdminRequestRow, id: string) =>
    id === "all"
      ? OPEN.includes(r.status)
      : id === "mine"
        ? isMine(r)
        : (STAGES.find((s) => s.id === id)?.match(r) ?? false);
  const count = (id: string) => items.filter((r) => inStage(r, id)).length;
  const overdue = open.filter((r) => slaOf(r, now).over);
  const awaitingVillageCount = count("village");

  const tabs = [
    ...(canReview ? [{ id: "mine", label: "ວຽກຂອງຂ້ອຍ", hot: true }] : []),
    { id: "all", label: "ທັງໝົດທີ່ຍັງຄ້າງ", hot: false },
    ...STAGES.map((s) => ({ id: s.id, label: s.label, hot: false })),
  ];

  const typeOptions = useMemo(() => {
    const ids = new Set(items.map((r) => r.document_type_id));
    return [...ids]
      .map((id) => ({ id, label: typeById.get(id)?.name_lo ?? id }))
      .sort((a, b) => a.label.localeCompare(b.label, "lo"));
  }, [items, typeById]);
  const villageOptions = useMemo(
    () =>
      [...new Set(items.map((r) => r.village_name_lo).filter((v): v is string => Boolean(v)))].sort((a, b) =>
        a.localeCompare(b, "lo"),
      ),
    [items],
  );

  const needle = q.trim().toLowerCase();
  const filtered = items
    .filter((r) => inStage(r, stage))
    .filter((r) => !overOnly || slaOf(r, now).over)
    .filter((r) => !typeFilter || r.document_type_id === typeFilter)
    .filter((r) => !village || r.village_name_lo === village)
    .filter((r) => {
      if (!needle) return true;
      const t = typeById.get(r.document_type_id);
      return [shortId(r.id), r.id, r.requester_name, r.document_number, t?.name_lo ?? "", t?.code ?? ""].some((x) =>
        x.toLowerCase().includes(needle),
      );
    })
    .sort((a, b) => {
      const d = new Date(stamp(a)).getTime() - new Date(stamp(b)).getTime();
      return newestFirst ? -d : d;
    });

  const issuedToday = today.data?.[today.data.length - 1]?.issued;

  return (
    <div className="q-page">
      <nav className="q-crumbs" aria-label="ທີ່ຢູ່">
        <Inbox size={14} /> ວຽກ <span>›</span> <strong>ຄິວຄຳຮ້ອງ</strong>
      </nav>
      <header className="q-head">
        <h1 className="q-head__title">ຄິວຄຳຮ້ອງ</h1>
        <p className="q-head__sub">
          ຕາມລຳດັບ ຍື່ນ → (ນາຍບ້ານຢັ້ງຢືນ) → ຜູ້ກວດເອກະສານ → ລົງນາມ → ອອກເອກະສານ · ຜູ້ຂໍຊຳລະຄ່າທຳນຽມຕອນຍື່ນ
          · ຂັ້ນນາຍບ້ານມີສະເພາະເອກະສານທີ່ຕ້ອງໃຫ້ນາຍບ້ານຢັ້ງຢືນ ແລະ ບ້ານທີ່ມີບັນຊີນາຍບ້ານ
        </p>
      </header>

      {!seesApplicantPII() ? (
        <p className="pii-note dx-gap-b">
          <Lock size={14} />
          <span>
            ຊື່ຜູ້ຂໍ ແລະ ຂໍ້ມູນສ່ວນບຸກຄົນຖືກບັງບາງສ່ວນ ສຳລັບບົດບາດນີ້ — ເບິ່ງໄດ້ຢ່າງດຽວ
          </span>
        </p>
      ) : null}

      <div className="dx-kpis dx-kpis--4">
        <KpiTile
          label="ລໍຜູ້ກວດເອກະສານ / ອອກເອກະສານ"
          value={queue.data ? num(count("submitted") + count("in_review") + count("signed")) : undefined}
          unit="ລາຍການ"
          tone="navy"
          hint={queue.data && awaitingVillageCount > 0 ? `ບໍ່ນັບ ລໍນາຍບ້ານ ${num(awaitingVillageCount)}` : undefined}
        />
        <KpiTile
          label="ເກີນກຳນົດ"
          value={queue.data ? num(overdue.length) : undefined}
          unit="ລາຍການ"
          tone={overdue.length > 0 ? "gold" : "green"}
          hint={
            queue.data
              ? `ຈາກທັງໝົດ ${num(open.length)} ທີ່ຍັງຄ້າງ · ເກີນ ${String(SLA_DAYS)} ມື້ນັບແຕ່ຍື່ນ ຫຼື ນາຍບ້ານເກີນກຳນົດ`
              : undefined
          }
        />
        <KpiTile
          label="ສົ່ງຄືນ ລໍຜູ້ຂໍແກ້ໄຂ"
          value={queue.data ? num(count("needs_changes")) : undefined}
          unit="ລາຍການ"
          tone="navy"
        />
        <KpiTile
          label="ອອກເອກະສານມື້ນີ້"
          value={issuedToday === undefined ? undefined : num(issuedToday)}
          unit="ສະບັບ"
          tone="navy"
          hint={today.dataUpdatedAt ? `ນັບຮອດ ${dateTimeLao(today.dataUpdatedAt).slice(11)}` : undefined}
        />
      </div>

      <div className="q-tabs" role="tablist" aria-label="ຂັ້ນຕອນ">
        {tabs.map((t) => (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={stage === t.id}
            className={stage === t.id ? "q-tab is-on" : "q-tab"}
            onClick={() => setParam("stage", t.id)}
          >
            {t.label}
            <span className={t.hot && count(t.id) > 0 ? "q-tab__ct is-hot num" : "q-tab__ct num"}>{num(count(t.id))}</span>
          </button>
        ))}
      </div>

      <div className="q-bar">
        <input
          type="search"
          className="input-field q-search"
          placeholder="ຄົ້ນຫາ ເລກຄຳຮ້ອງ / ຊື່ຜູ້ຂໍ / ປະເພດ"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          aria-label="ຄົ້ນຫາ"
        />
        <select className="filter-select" value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} aria-label="ປະເພດເອກະສານ">
          <option value="">ທຸກປະເພດເອກະສານ</option>
          {typeOptions.map((t) => (
            <option key={t.id} value={t.id}>
              {t.label}
            </option>
          ))}
        </select>
        <select className="filter-select" value={village} onChange={(e) => setVillage(e.target.value)} aria-label="ບ້ານ">
          <option value="">ທຸກບ້ານ</option>
          {villageOptions.map((v) => (
            <option key={v} value={v}>
              ບ້ານ{v}
            </option>
          ))}
        </select>
        <label className="q-check">
          <input type="checkbox" checked={overOnly} onChange={(e) => setParam("over", e.target.checked ? "1" : null)} />
          ສະເພາະເກີນກຳນົດ
        </label>
        <span className="q-bar__spacer" />
        <button type="button" className="q-sort" onClick={() => setNewestFirst((v) => !v)}>
          <ArrowUpDown size={14} /> {newestFirst ? "ເລື່ອງໃໝ່ສຸດກ່ອນ" : "ເລື່ອງເກົ່າສຸດກ່ອນ"}
        </button>
      </div>

      <section className="table-scroll q-table-wrap">
        {queue.isPending || types.isPending ? <div className="data-table__empty body-sm">ກຳລັງໂຫຼດ…</div> : null}
        {queue.isError ? (
          <div className="data-table__empty body-sm">
            <span className="field-error">ໂຫຼດບໍ່ສຳເລັດ: {queue.error.message}</span>{" "}
            <button type="button" className="btn-outline" onClick={() => void queue.refetch()}>
              ລອງໃໝ່
            </button>
          </div>
        ) : null}
        {queue.data && types.data ? (
          <table className="data-table q-table">
            <thead>
              <tr>
                <th>ຄຳຮ້ອງ</th>
                <th>ຜູ້ຂໍ</th>
                <th>ວິທີຮັບ</th>
                <th>ຍື່ນເມື່ອ</th>
                <th>ຂັ້ນຕອນ</th>
                <th>ກຳນົດເວລາ</th>
                <th>ຜູ້ຮັບຜິດຊອບ</th>
                <th className="is-right">ຄ່າທຳນຽມ</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={9} className="data-table__empty">
                    ບໍ່ມີຄຳຮ້ອງທີ່ກົງກັບຕົວກອງນີ້
                  </td>
                </tr>
              ) : null}
              {filtered.map((r) => {
                const type = typeById.get(r.document_type_id);
                const sla = slaOf(r, now);
                const go = () => void navigate(`/requests/${r.id}`);
                return (
                  <tr key={r.id} className={sla.over ? "q-row is-over" : "q-row"} onClick={go}>
                    <td>
                      <div className="q-main">{type?.name_lo ?? "—"}</div>
                      <div className="q-sub num">
                        {type?.code} · #{shortId(r.id)}
                      </div>
                    </td>
                    <td>
                      <div>{r.requester_name || "—"}</div>
                      <div className="q-sub">{r.village_name_lo ? `ບ້ານ${r.village_name_lo}` : "ບໍ່ລະບຸບ້ານ"}</div>
                    </td>
                    <td>
                      <DeliveryBadge row={r} />
                    </td>
                    <td>
                      <div>{formatAgoLo(stamp(r))}</div>
                      <div className="q-sub num">{dateTimeLao(new Date(stamp(r)).getTime())}</div>
                    </td>
                    <td>
                      <StageBadge row={r} />
                      {r.village_certification?.status === "unrecognized" ? (
                        <div>
                          <span className="q-badge q-badge--err">ນາຍບ້ານບໍ່ຮູ້ຈັກຜູ້ຂໍ</span>
                        </div>
                      ) : null}
                    </td>
                    <td>
                      <strong className={`q-sla q-sla--${sla.tone}`}>{sla.text}</strong>
                    </td>
                    <td>
                      {awaitingVillage(r) ? (
                        <span>ນາຍບ້ານ{r.village_name_lo ?? ""}</span>
                      ) : r.assigned_officer_id && r.assigned_officer_id === me ? (
                        <strong className="q-you">ທ່ານ</strong>
                      ) : r.assigned_officer_name ? (
                        r.assigned_officer_name
                      ) : (
                        <span className="q-faint">ຍັງບໍ່ມີຄົນຮັບ</span>
                      )}
                    </td>
                    <td className="is-right num">{r.paid_lak === null ? <span className="q-faint">—</span> : num(r.paid_lak)}</td>
                    <td className="is-right">
                      {canClaim && awaitingVillage(r) && !villageOverdue(r.village_certification, now) ? (
                        <button
                          type="button"
                          className="btn-outline q-act"
                          title="ຂ້າມຂັ້ນນາຍບ້ານ (ຕ້ອງໃສ່ເຫດຜົນ)"
                          onClick={(e) => {
                            e.stopPropagation();
                            setWaiving(r);
                          }}
                        >
                          ຂ້າມຂັ້ນ
                        </button>
                      ) : canClaim && r.status === "submitted" ? (
                        <button
                          type="button"
                          className="btn-primary q-act"
                          title={
                            awaitingVillage(r) ? "ນາຍບ້ານເກີນກຳນົດ — ຮັບແລ້ວລະບົບຂ້າມຂັ້ນນາຍບ້ານໃຫ້" : undefined
                          }
                          disabled={claim.isPending}
                          onClick={(e) => {
                            e.stopPropagation();
                            claim.mutate(r.id);
                          }}
                        >
                          ຮັບ
                        </button>
                      ) : null}
                      {!(canClaim && r.status === "submitted") ? (
                        <Link to={`/requests/${r.id}`} className="btn-outline q-act" onClick={(e) => e.stopPropagation()}>
                          ເບິ່ງ
                        </Link>
                      ) : null}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        ) : null}
      </section>
      <p className="q-foot">
        ພົບ {num(filtered.length)} ລາຍການ · ຄ່າທຳນຽມ = ຍອດທີ່ຜູ້ຂໍຊຳລະແລ້ວ (ກີບ) · ກຳນົດເວລາ = {String(SLA_DAYS)} ມື້ນັບແຕ່ວັນຍື່ນ
      </p>
      {awaitingVillageCount > 0 ? (
        <p className="q-foot">
          ລໍນາຍບ້ານຢັ້ງຢືນ: ຕາແສງຍັງຮັບບໍ່ໄດ້ຈົນກວ່ານາຍບ້ານຢັ້ງຢືນ ຫຼື ເກີນກຳນົດ (2 ວັນເຮັດການ) — ເກີນກຳນົດແລ້ວກົດ ຮັບ
          ໄດ້ເລີຍ ລະບົບຂ້າມຂັ້ນນາຍບ້ານໃຫ້ · ຂ້າມກ່ອນກຳນົດຕ້ອງໃສ່ເຫດຜົນ
        </p>
      ) : null}
      {claim.isError ? <p className="field-error">ຮັບຄຳຮ້ອງບໍ່ສຳເລັດ: {claim.error.message}</p> : null}
      {queue.data?.hasMore ? <p className="q-foot">ສະແດງ 200 ລາຍການລ້າສຸດ — ອາດຍັງບໍ່ຄົບ</p> : null}
      {waiving ? (
        <VillageWaiveModal requestId={waiving.id} villageName={waiving.village_name_lo} onClose={() => setWaiving(null)} />
      ) : null}
    </div>
  );
}
